import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Locale as I18nLocale } from '@medable/i18n-core-v4';
import { Locale } from 'libs.i18n-core';
import { MessageResource } from 'libs.nucleus';
import { detectUserLanguage } from 'libs.react';
import { getStoredPseudoLocale } from '../../helpers/pseudo_locale_storage';
// translations imported here should match available locales based on https://gitlab.medable.com/l10n/continuous-localization/-/blob/main/config.json?ref_type=heads#L128
const translationsMap = {
    'en-US': () => import('@medable/localization-product-web-monorepo-bundled/en-US.json'),
    'es-US': () => import('@medable/localization-product-web-monorepo-bundled/es-US.json'),
    'fr-FR': () => import('@medable/localization-product-web-monorepo-bundled/fr-FR.json'),
    'ja-JP': () => import('@medable/localization-product-web-monorepo-bundled/ja-JP.json'),
    'ko-KR': () => import('@medable/localization-product-web-monorepo-bundled/ko-KR.json'),
    'zh-Hans-CN': () => import('@medable/localization-product-web-monorepo-bundled/zh-Hans-CN.json'),
    'zh-Hant-TW': () => import('@medable/localization-product-web-monorepo-bundled/zh-Hant-TW.json'),
};
const getTranslationsLocale = (locale) => (new I18nLocale(locale).isPseudoLocale ? Locale.en_US : locale);
export const useMessageProviderForDetectedLocale = () => {
    const [bundles, setBundles] = useState({});
    const loadingLocales = useRef(new Set());
    const browserLocale = useMemo(() => detectUserLanguage({
        supportedLocales: Object.keys(translationsMap),
        fallbackLocale: Locale.en_US,
    }), []);
    // A persisted pseudo-locale (localization-testing aid) is never stored on the identity, so honor
    // it here at startup and give it precedence over the browser/identity locale.
    const initialLocale = useMemo(() => { var _a; return (_a = getStoredPseudoLocale()) !== null && _a !== void 0 ? _a : browserLocale; }, [browserLocale]);
    const detectedLocale = initialLocale;
    // Loads (and caches) the translation bundle for the given locale. The locale that ultimately
    // drives the UI is not known at mount: AuthProvider seeds it from the stored identity once the
    // session resolves. Bundles are therefore fetched lazily per requested locale rather than only
    // for the browser-detected one, so switching locale (on load or via the profile editor) pulls in
    // the matching translations.
    const ensureBundleLoaded = useCallback((locale) => {
        const translationsLocale = getTranslationsLocale(locale);
        const loadTranslations = translationsMap[translationsLocale];
        if (!loadTranslations || bundles[translationsLocale] || loadingLocales.current.has(translationsLocale)) {
            return;
        }
        loadingLocales.current.add(translationsLocale);
        loadTranslations()
            .then((module) => {
            setBundles((previous) => (Object.assign(Object.assign({}, previous), { [translationsLocale]: module.default || {} })));
        })
            .finally(() => {
            loadingLocales.current.delete(translationsLocale);
        });
    }, [bundles]);
    // Preload the browser-detected locale so the first paint (before the identity resolves) is localized.
    useEffect(() => {
        ensureBundleLoaded(detectedLocale);
    }, [detectedLocale, ensureBundleLoaded]);
    // this can be updated in the future to include translations from firebase and other sources
    const messageProvider = useCallback((locale) => {
        ensureBundleLoaded(locale);
        const translationsLocale = getTranslationsLocale(locale);
        return new MessageResource(locale, bundles[translationsLocale] || {});
    }, [bundles, ensureBundleLoaded]);
    return {
        detectedLocale,
        messageProvider,
    };
};
